/// <amd-module name="Build/Pipelines/PipelinesFolderView" />
import { BuildSignalRActionCreator, BuildSignalRActionHub } from "Actions/SignalR";
import { Folder } from "Build/Client/Contracts/Build";
import { PipelineDataList } from "Build/Client/Contracts/DataProvider";
import { IBuildLinkingService } from "Build/Client/Linking";
import { IDeleteDefinitionProps } from "Build/Common/Commands/DeleteDefinition/Command.types";
import { IPermissionsProps } from "Build/Common/Commands/Permissions/Command.types";
import { ISaveDefinitionProps } from "Build/Common/Commands/SaveDefinition/Command.types";
import { CommandComponent } from "Build/Common/Library/Components/CommandComponent";
import { registerErrorHandler } from "Build/Common/Library/ErrorHandler";
import { BuildRequiredPermissions, Security } from "Build/Common/Library/Security";
import { clearTreeStateOnUnmount, getTreeState } from "Build/Common/Library/StateHandler";
import { PermissionsStore } from "Build/Common/Library/Stores/Permissions";
import { ContributionDataActionHub } from "Build/Flux/Actions/ContributionData";
import { DefinitionFavoritesActionCreator, DefinitionFavoritesActionHub } from "Build/Pipelines/Actions/Favorites";
import { PipelineFilterKeys } from "Build/Pipelines/Constants";
import { Performance } from "Build/Pipelines/Performance";
import { PipelinesStore } from "Build/Pipelines/PipelinesStore";
import * as Resources from "Build/Pipelines/Resources";
import { BuildSignalRHub } from "Build/Pipelines/SignalRHub";
import { PipelinesTreeView } from "Build/Pipelines/Tabs/Pipelines/PipelinesTreeView";
import { IFavoritesService } from "Favorites/Common/FavoritesService";
import { GlobalMessageLevel, IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { RunsFilterBar } from "Tabs/Runs/RunsFilterBar";
import { RunsTable } from "Tabs/Runs/RunsTable";
import { RunsAppliedFilterStore } from "Tabs/Runs/Stores/RunsAppliedFilterStore";
import { getTFSPageData } from "Tfs/Platform/Page";
import { IObservableArray, IObservableValue, IReadonlyObservableArray, ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssHistoryService } from "VSS/Platform/History";
import { Components, IVssComponentContext, IVssComponentProperties, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { IStartRightExtensionService } from "VSS/Platform/StartRightExtension";
import { Header, TitleSize } from "VSSUI/Header";
import { HeaderCommandBar, IHeaderCommandBarItem, getFilterItem } from "VSSUI/HeaderCommandBar";
import { IMenuItem } from "VSSUI/Menu";
import { IObservableViewStateUrl } from "VSSUI/Navigation/ViewStateNavigation";
import { IVssHubViewState, VssHubViewState } from "VSSUI/Navigation/VssHubViewState";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { IVssContributedTab, TabBar, TabContent, TabSize } from "VSSUI/Tabs";
import { InlineKeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { Filter, IFilterState } from "VSSUI/Utilities/Filter";
import * as React from "react";

namespace Tabs {
    export const Runs = "runs";
    export const Pipelines = "pipelines";
}

export interface IPipelinesFolderViewContentProps extends IVssComponentProperties {}

export interface IPipelinesFolderViewContentState {
    tabs: IReadonlyObservableArray<IVssContributedTab>;
    filterHasChanges: ObservableValue<boolean>;
}

class PipelinesFolderViewContent extends VssComponent<IPipelinesFolderViewContentProps, IPipelinesFolderViewContentState> {
    private _isPageLoad = true;
    private _runsFilter: Filter;
    private _runsAppliedFilterStore: RunsAppliedFilterStore;
    private _signalRHub: BuildSignalRHub;
    private _signalRActionHub: BuildSignalRActionHub;
    private _runsFilterToggled: ObservableValue<boolean>;
    private _security: Security;
    private _permissions: PermissionsStore;
    private _folderPath: IObservableValue<string | undefined>;
    private _ttiMarked: boolean = false;
    private _hubViewState: IVssHubViewState;
    private _runsUrl: IObservableViewStateUrl;
    private _foldersUrl: IObservableViewStateUrl;
    private _pipelineFilter: Filter;
    private _favoritesActionHub: DefinitionFavoritesActionHub;
    private _favoritesActionCreator: DefinitionFavoritesActionCreator;
    private _store: PipelinesStore;
    private _treeState: string = "";

    constructor(props: IPipelinesFolderViewContentProps, context: IVssComponentContext) {
        super(props, context);

        this._folderPath = new ObservableValue(this._getPath());

        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const historyState = historyService.getState();

        // the PageStateHistoryService will add our filter state into the URL, with each key being named something like "statusFilter"
        // if any of these keys exist, we should show the filterbar so it can be initialized with this state
        const filterBarInitiallyVisible = Object.keys(historyState).some(key => key.endsWith("Filter"));

        this._runsFilter = new Filter();
        this._runsFilterToggled = new ObservableValue<boolean>(filterBarInitiallyVisible);

        this._runsAppliedFilterStore = new RunsAppliedFilterStore(this._runsFilter);

        const tfsPageData = getTFSPageData(this.context.pageContext);
        const vssPageService = context.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();

        this._signalRActionHub = new BuildSignalRActionHub();
        if (tfsPageData && tfsPageData.project && vssPageData) {
            this._signalRHub =
                this.props.signalRHub ||
                new BuildSignalRHub({
                    actionCreator: new BuildSignalRActionCreator({ actionHub: this._signalRActionHub }),
                    hostInformation: {
                        hostId: vssPageData.hostId,
                        isHosted: vssPageData.isHosted,
                        hostPath: vssPageData.hostPath,
                        projectId: tfsPageData.project.id
                    },
                    pageContext: this.context.pageContext,
                    projectId: tfsPageData.project.id
                });
        }

        this._permissions = new PermissionsStore({
            pageContext: context.pageContext
        });

        this._security = new Security({
            projectId: tfsPageData && tfsPageData.project ? tfsPageData.project.id : "",
            store: this._permissions
        });

        this._favoritesActionHub = new DefinitionFavoritesActionHub();
        if (tfsPageData && tfsPageData.project && vssPageData) {
            this._favoritesActionCreator = new DefinitionFavoritesActionCreator({
                actionHub: this._favoritesActionHub,
                favoritesService: context.pageContext.getService<IFavoritesService>("IFavoritesService"),
                project: tfsPageData.project,
                currentUser: vssPageData.user
            });
        }

        this._pipelineFilter = new Filter();
        const pageHistoryService = this.context.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        pageHistoryService.registerFilter(this._pipelineFilter, "pipeline", [
            {
                dataType: "string",
                filterKey: PipelineFilterKeys.NameFilter,
                historyBehavior: HistoryBehavior.replace
            }
        ]);

        this._hubViewState = new VssHubViewState(context.pageContext, { defaultPivot: Tabs.Pipelines });
        this._runsUrl = this._hubViewState.createObservableUrl({ view: Tabs.Runs });
        this._foldersUrl = this._hubViewState.createObservableUrl({ view: Tabs.Pipelines });

        this._treeState = getTreeState(this.context.pageContext);

        this._store = new PipelinesStore({
            pageContext: context.pageContext,
            contributionActionHub: new ContributionDataActionHub<PipelineDataList>(),
            favoritesActionHub: this._favoritesActionHub,
            signalRActionHub: this._signalRActionHub,
            pipelinesFilter: this._pipelineFilter,
            rootFolder: this._folderPath.value ? this._folderPath.value : "\\",
            // If showing folder view on page load, the store should not treat initial data as recent definitions
            ignoreInitialData: this._hubViewState.selectedPivot.value === Tabs.Pipelines
        });

        this.state = {
            tabs: this._getTabs(),
            filterHasChanges: new ObservableValue(false)
        };
    }

    public render() {
        return (
            <Observer folderPath={this._folderPath}>
                {(observerProps: { folderPath: string }) => {
                    return (
                        <Surface background={SurfaceBackground.neutral}>
                            <Page className="pipelines-page" selectedTabId={this._hubViewState.selectedPivot} tabProviders={this.state.tabs}>
                                <Header
                                    title={this._getFolderName(observerProps.folderPath)}
                                    titleSize={TitleSize.Large}
                                    commandBarItems={this._getCommandBarItems()}
                                    titleIconProps={{
                                        iconName: "FabricFolderFill",
                                        className: "pipelines-folder-icon font-size-ll"
                                    }}
                                    backButtonProps={{ onClick: this._onBackClick }}
                                />
                                <TabBar tabSize={TabSize.Tall} onSelectedTabChanged={this._onTabChanged} />
                                <TabContent />
                            </Page>
                        </Surface>
                    );
                }}
            </Observer>
        );
    }

    public componentDidMount() {
        this._signalRHub.watchProject();
        this._runsAppliedFilterStore.filter.subscribe(this._onFilterChanged);
        registerErrorHandler((error: Error) => {
            if (error) {
                const globalMessageService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
                globalMessageService.addBanner({
                    message: error.message,
                    level: GlobalMessageLevel.error
                });
            }
        });
        this._markTTI();
    }

    private _onBackClick = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void => {
        const pageContext = this.context.pageContext;
        const linkingService = pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const path = this._getPath();

        // The regex targets trailing slashes which it removes
        const pathParts = path.replace(/(\\)+$/, "").split("\\");

        let url: string;
        if (pathParts.length > 2) {
            url = linkingService.getFolderUrl(pathParts.slice(0, length - 1).join("\\"));
        } else {
            url = linkingService.getFolderViewUrl();
        }

        onClickFPS(pageContext, url, true);
    };

    private _markTTI() {
        if (!this._ttiMarked) {
            new Performance(this.context.pageContext).mark("folderView", undefined, this._folderPath.value);
            this._ttiMarked = true;
        }
    }

    public componentWillUnmount() {
        this._signalRHub.dispose();
        this._runsAppliedFilterStore.filter.unsubscribe(this._onFilterChanged);
        registerErrorHandler((error: Error) => {});
        clearTreeStateOnUnmount(this.context.pageContext);
    }

    private _getFolderName = (path: string) => {
        if (!path) {
            return "";
        }
        const pathParts = path
            .replace(/(\\)+$/, "")
            .split("\\")
            .reverse();
        return pathParts && pathParts.length > 0 ? pathParts[0] : "";
    };

    private _getPath = (): string => {
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");

        const currentState = historyService.getState();
        let pathFromState = currentState["definitionScope"] ? currentState["definitionScope"] : currentState["path"];

        if (pathFromState.length > 1 && pathFromState.endsWith("\\")) {
            pathFromState = pathFromState.substr(0, pathFromState.length - 1);
        }

        return pathFromState;
    };

    private _onTabChanged = (tabKey: string) => {
        this._isPageLoad = false;
        new Performance(this.context.pageContext).onFPS();
        this._hubViewState.selectedPivot.value = tabKey;
    };

    private _onFilterChanged = (value: IFilterState, action: string) => {
        this.state.filterHasChanges.value = this._runsAppliedFilterStore.filter.hasChangesToReset();
    };

    private _getTabs = (): IReadonlyObservableArray<IVssContributedTab> => {
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const historyState = historyService.getState();

        // the PageStateHistoryService will add our filter state into the URL, with each key being named something like "statusFilter"
        // if any of these keys exist, we should show the filterbar so it can be initialized with this state
        const filterBarInitiallyVisible = Object.keys(historyState).some(key => key.endsWith("Filter") && key !== "pipelineNameFilter");

        const showFilterBar = new ObservableValue(filterBarInitiallyVisible);

        const tabs = new ObservableCollection<IVssContributedTab>();
        tabs.push([
            {
                id: Tabs.Pipelines,
                name: Resources.PipelinesTabName,
                order: 2,
                url: this._foldersUrl.value,
                render: () => (
                    <div className="page-content page-content-top">
                        <PipelinesTreeView
                            store={this._store}
                            signalRHub={this._signalRActionHub}
                            security={this._security}
                            favoritesActionCreator={this._favoritesActionCreator}
                            isPageLoad={this._isPageLoad}
                            treeState={this._treeState}
                            rootPath={this._folderPath.value ? this._folderPath.value : "\\"}
                        />
                    </div>
                ),
                renderFilterBar: (inTabBar: boolean) => {
                    if (inTabBar) {
                        return (
                            <div className="definitions-filterbar flex-row">
                                <InlineKeywordFilterBarItem
                                    filter={this._pipelineFilter}
                                    placeholder={Resources.PipelinesFilterPlaceholderText}
                                    filterItemKey={PipelineFilterKeys.NameFilter}
                                    maxTextLength={50}
                                />
                            </div>
                        );
                    }
                }
            },
            {
                id: Tabs.Runs,
                name: Resources.RunsTabName,
                order: 3,
                url: this._runsUrl.value,
                render: () => (
                    <>
                        <Observer showFilterBar={showFilterBar}>
                            {(props: { showFilterBar: boolean }) => {
                                if (props.showFilterBar) {
                                    return <RunsFilterBar appliedFilterStore={this._runsAppliedFilterStore} definitionId={undefined} />;
                                }
                                return <></>;
                            }}
                        </Observer>
                        <div className="page-content page-content-top">
                            <RunsTable
                                appliedFilterStore={this._runsAppliedFilterStore}
                                isPageLoad={this._isPageLoad}
                                signalRActionHub={this._signalRActionHub}
                                security={this._security}
                                definitionScope={this._folderPath.value}
                            />
                        </div>
                    </>
                ),
                renderFilterBar: inTabBar => {
                    if (inTabBar) {
                        return (
                            <Observer filterHasChanges={this.state.filterHasChanges}>
                                {(props: { filterHasChanges: boolean }) => {
                                    return (
                                        <HeaderCommandBar
                                            items={[getFilterItem(() => (showFilterBar.value = !showFilterBar.value), props.filterHasChanges)]}
                                        />
                                    );
                                }}
                            </Observer>
                        );
                    }
                }
            }
        ] as IVssContributedTab[]);

        return tabs;
    };

    private _getCommandBarItems = (): IHeaderCommandBarItem[] | IObservableArray<IHeaderCommandBarItem> => {
        const vssPageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();
        const tfsPageData = getTFSPageData(this.context.pageContext);
        if (!vssPageData || !tfsPageData || !tfsPageData.project) {
            return [];
        }

        if (!this._security.isMember()) {
            return [];
        }

        const definitionPath: string = this._folderPath.value || "";
        const projectId: string = tfsPageData.project.id;

        const pageContext = this.context.pageContext;

        const linkingService = pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const startRightExtensionService = pageContext.getService<IStartRightExtensionService>("IStartRightExtensionService");
        const newPipelineLink = startRightExtensionService.getCreatePipelineUrlByPipelineFolder(pageContext, definitionPath) ||
                                linkingService.getNewDefinitionEditUrl(definitionPath);

        let builtInActions: IHeaderCommandBarItem[] = [];

        builtInActions.push({
            text: Resources.Security,
            id: "folder-menu-security",
            important: true,
            onActivate: () => {
                const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                layoutManager.renderCallout(onDismiss => {
                    const props: IPermissionsProps = {
                        path: definitionPath
                    };
                    return React.createElement(
                        CommandComponent,
                        {
                            commandType: "ciPermissionsComponent",
                            ...props
                        },
                        null
                    );
                });
            }
        });

        if (this._security.hasFolderPermission(definitionPath, BuildRequiredPermissions.EditBuildDefinition)) {
            builtInActions.push({
                id: "new-pipeline-command",
                important: true,
                isPrimary: true,
                href: newPipelineLink,
                onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    onClickFPS(pageContext, newPipelineLink, true, event);
                },
                text: Resources.NewPipeline
            });
        }

        builtInActions.push({
            text: Resources.Rename,
            id: "folder-menu-rename",
            important: false,
            onActivate: () => {
                const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                layoutManager.renderCallout(onDismiss => {
                    const props: ISaveDefinitionProps = {
                        pageContext: this.context.pageContext,
                        folder: {
                            path: definitionPath
                        },
                        projectId: projectId,
                        saveAsTemplate: false,
                        onClose: onDismiss,
                        onFolderSaved: (folder: Folder, previousPath: string) => {
                            this._folderPath.value = folder.path;
                            onClickFPS(this.context.pageContext, linkingService.getFolderUrl(folder.path), true);
                        }
                    };
                    return React.createElement(
                        CommandComponent,
                        {
                            commandType: "ciSaveDefinitionComponent",
                            ...props
                        },
                        null
                    );
                });
            }
        });

        builtInActions.push({
            text: Resources.Delete,
            id: "folder-menu-delete",
            important: false,
            onActivate: () => {
                const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                layoutManager.renderCallout(onDismiss => {
                    const props: IDeleteDefinitionProps = {
                        pageContext: this.context.pageContext,
                        path: definitionPath,
                        projectId: projectId,
                        onClose: onDismiss,
                        onFolderDeleted: (path: string) => {
                            this._onFolderDeleted();
                        }
                    };
                    return React.createElement(
                        CommandComponent,
                        {
                            commandType: "ciDeleteDefinitionComponent",
                            ...props
                        },
                        null
                    );
                });
            }
        });

        return builtInActions;
    };

    private _onFolderDeleted = () => {
        const linkingService = this.context.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const landingPageLink = linkingService.getDefinitionUrl(undefined);
        onClickFPS(this.context.pageContext, landingPageLink, true);
    };
}

Components.add("pipelines-folder", PipelinesFolderViewContent);
